// ===========================================
// Bank Transfer Dialog
// ===========================================
// Modal showing bank transfer details and payment instructions

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Paper,
  IconButton,
  Divider,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useSnackbar } from 'notistack';
import { BankTransferDetails } from '../../api/invoices.api';

// ===========================================
// HELPER FUNCTIONS
// ===========================================

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(num);
}

// ===========================================
// COMPONENT PROPS
// ===========================================

interface BankTransferDialogProps {
  open: boolean;
  onClose: () => void;
  bankDetails: BankTransferDetails | null;
  invoiceNumber: string;
  amountDue: number;
}

// ===========================================
// COMPONENT
// ===========================================

export default function BankTransferDialog({
  open,
  onClose,
  bankDetails,
  invoiceNumber,
  amountDue,
}: BankTransferDialogProps) {
  const { enqueueSnackbar } = useSnackbar();

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar(`${label} copied to clipboard`, { variant: 'success' });
  };

  if (!bankDetails) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceIcon color="primary" />
            <Typography variant="h6">Pay by Bank Transfer</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info">
            Bank transfer details have not been configured by the school. Please contact the school
            directly for payment options.
          </Alert>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceIcon color="primary" />
          <Typography variant="h6">Pay by Bank Transfer</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Payment Amount */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 3,
            bgcolor: '#FCF6E6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Amount Due
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#4580E4' }}>
              {formatCurrency(amountDue)}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => handleCopy(amountDue.toFixed(2), 'Amount')}
            title="Copy amount"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Paper>

        {/* Bank Details */}
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          Bank Account Details
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Account Name */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Account Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {bankDetails.accountName}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => handleCopy(bankDetails.accountName, 'Account name')}
                title="Copy account name"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>

            <Divider />

            {/* BSB */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  BSB
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                  {bankDetails.bsb}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => handleCopy(bankDetails.bsb.replace(/-/g, ''), 'BSB')}
                title="Copy BSB"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>

            <Divider />

            {/* Account Number */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Account Number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                  {bankDetails.accountNumber}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => handleCopy(bankDetails.accountNumber, 'Account number')}
                title="Copy account number"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Paper>

        {/* Payment Reference */}
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          Payment Reference
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Please use this reference when making your payment
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, fontFamily: 'monospace', color: '#4580E4' }}
            >
              {invoiceNumber}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => handleCopy(invoiceNumber, 'Invoice number')}
            title="Copy invoice number"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Paper>

        {/* Instructions */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            Important Instructions:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
            <li>Use the invoice number as your payment reference</li>
            <li>Bank transfers may take 1-2 business days to process</li>
            <li>Your invoice will be marked as paid once the payment is received</li>
          </Typography>
        </Alert>
      </DialogContent>
    </Dialog>
  );
}
